// hooks/useScraperLogs.js - WebSocket client hook for real-time scraper logs
import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import apiClient from '../lib/apiClient';
import { supabase } from '../config/supabase';
import { onAppResume, SOCKET_ORIGIN } from '../platform';

const MAX_LOGS = 500;
const RECONNECT_DELAY_BASE = 1000;
const RECONNECT_DELAY_MAX = 30000;

// Get WebSocket URL from environment or derive from API.
// Native (capacitor://localhost) has no local backend — use the deployed
// backend, never the localhost:10000 dev fallback.
const getSocketUrl = () => SOCKET_ORIGIN;

export function useScraperLogs() {
  const [logs, setLogs] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [usingPolling, setUsingPolling] = useState(false);

  const socketRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const pollingIntervalRef = useRef(null);

  // Add a single log entry, maintaining max buffer size
  const addLog = useCallback((log) => {
    setLogs(prev => {
      // Check for duplicates by id
      if (prev.some(l => l.id === log.id)) return prev;

      const newLogs = [log, ...prev];
      if (newLogs.length > MAX_LOGS) {
        newLogs.pop();
      }
      return newLogs;
    });
  }, []);

  // Add multiple logs (for history)
  const addLogs = useCallback((newLogs) => {
    setLogs(prev => {
      const existingIds = new Set(prev.map(l => l.id));
      const uniqueNew = newLogs.filter(l => !existingIds.has(l.id));
      const combined = [...uniqueNew, ...prev];
      return combined.slice(0, MAX_LOGS);
    });
  }, []);

  // Clear all logs (local + server)
  const clear = useCallback(() => {
    setLogs([]);
    // Also clear server-side log buffer
    apiClient.del('/api/scraper/logs').catch(() => {});
  }, []);

  // Fallback to REST polling when WebSocket fails
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    setUsingPolling(true);
    console.log('[ScraperLogs] Falling back to REST polling');

    const poll = async () => {
      try {
        const data = await apiClient.get('/api/scraper/logs');
        if (data.logs) {
          addLogs(data.logs);
        }
        if (data.stats) {
          setStats(data.stats);
        }
        setError(null);
      } catch (err) {
        setError('Polling failed: ' + err.message);
      }
    };

    // Initial poll
    poll();

    // Poll every 3 seconds
    pollingIntervalRef.current = setInterval(poll, 3000);
  }, [addLogs]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setUsingPolling(false);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    // Don't connect if already connected
    if (socketRef.current?.connected) return;

    const socketUrl = getSocketUrl();
    console.log('[ScraperLogs] Connecting to', socketUrl);

    // Get the auth token from the Supabase client, not localStorage directly:
    // the native shell stores the session in Capacitor Preferences (see
    // src/platform/preferencesStorage.js), so raw localStorage reads would
    // silently return no token there.
    let authToken = null;
    try {
      const { data } = (await supabase?.auth.getSession()) ?? {};
      authToken = data?.session?.access_token ?? null;
    } catch (e) {
      console.warn('[ScraperLogs] Could not get auth token:', e.message);
    }

    // getSession is async — another connect may have won the race meanwhile
    if (socketRef.current?.connected) return;

    const socket = io(`${socketUrl}/scraper-logs`, {
      auth: { token: authToken },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: RECONNECT_DELAY_BASE,
      reconnectionDelayMax: RECONNECT_DELAY_MAX,
      timeout: 10000
    });

    socket.on('connect', () => {
      console.log('[ScraperLogs] Connected');
      setConnected(true);
      setError(null);
      reconnectAttemptRef.current = 0;
      stopPolling();
    });

    socket.on('disconnect', (reason) => {
      console.log('[ScraperLogs] Disconnected:', reason);
      setConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('[ScraperLogs] Connection error:', err.message);
      reconnectAttemptRef.current++;

      if (reconnectAttemptRef.current >= 3) {
        setError('WebSocket unavailable, using polling');
        startPolling();
      } else {
        setError('Connecting...');
      }
    });

    // Handle incoming log
    socket.on('log', (entry) => {
      addLog(entry);
    });

    // Handle log history on connect
    socket.on('history', (history) => {
      console.log('[ScraperLogs] Received history:', history.length, 'entries');
      addLogs(history);
    });

    // Handle stats update
    socket.on('stats', (newStats) => {
      setStats(newStats);
    });

    // Handle logs cleared event
    socket.on('logs-cleared', () => {
      clear();
    });

    socket.on('error', (err) => {
      console.error('[ScraperLogs] Socket error:', err);
      setError(err.message || 'Socket error');
    });

    socketRef.current = socket;
  }, [addLog, addLogs, clear, startPolling, stopPolling]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    stopPolling();
    setConnected(false);
  }, [stopPolling]);

  // Request more history
  const requestHistory = useCallback((options = {}) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('get-history', options);
    }
  }, []);

  // Request current stats
  const requestStats = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('get-stats');
    }
  }, []);

  // Filter logs by criteria
  const getFilteredLogs = useCallback((filter = {}) => {
    let filtered = logs;

    if (filter.level) {
      filtered = filtered.filter(l => l.level === filter.level);
    }
    if (filter.operation) {
      filtered = filtered.filter(l => l.context?.operation === filter.operation);
    }
    if (filter.jobId) {
      filtered = filtered.filter(l => l.context?.jobId === filter.jobId);
    }
    if (filter.search) {
      const search = filter.search.toLowerCase();
      filtered = filtered.filter(l =>
        l.message?.toLowerCase().includes(search) ||
        JSON.stringify(l.context)?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [logs]);

  // Fetch historical logs from database
  const fetchHistoricalLogs = useCallback(async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.limit) params.set('limit', options.limit);
      if (options.offset) params.set('offset', options.offset);
      if (options.level) params.set('level', options.level);
      if (options.operation) params.set('operation', options.operation);
      if (options.jobId) params.set('jobId', options.jobId);
      if (options.since) params.set('since', options.since);
      if (options.until) params.set('until', options.until);
      if (options.search) params.set('search', options.search);

      const url = `/api/scraper/logs/history${params.toString() ? '?' + params.toString() : ''}`;
      const data = await apiClient.get(url);
      return data;
    } catch (err) {
      console.error('[ScraperLogs] Historical fetch error:', err);
      throw err;
    }
  }, []);

  // Fetch recent jobs from database
  const fetchRecentJobs = useCallback(async (days = 7) => {
    try {
      const data = await apiClient.get(`/api/scraper/logs/jobs?days=${days}`);
      return data.jobs || [];
    } catch (err) {
      console.error('[ScraperLogs] Jobs fetch error:', err);
      throw err;
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    // iOS shell: the OS freezes socket timers while suspended, so the
    // client can come back half-dead. Reconnect on foreground resume
    // (no-op unsubscribe on web).
    const offResume = onAppResume(() => {
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.connect();
      } else if (!socketRef.current) {
        connect();
      }
    });
    return () => {
      offResume();
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    logs,
    connected,
    error,
    stats,
    usingPolling,
    clear,
    connect,
    disconnect,
    requestHistory,
    requestStats,
    getFilteredLogs,
    fetchHistoricalLogs,
    fetchRecentJobs
  };
}

export default useScraperLogs;
